import { Phone as PhoneIcon, MessageCircle } from 'lucide-react';

import type { Contact } from '@/types/contacts';
import { EMPTY, formatFullTimestamp } from '@/lib/contacts';

export function ExpandedDetail({ contact }: { contact: Contact }) {
  const firedGoogle = contact.conversions.some(
    (c) => c.platform === 'google_ads' && c.status === 'sent',
  );
  const firedMeta = contact.conversions.some(
    (c) => c.platform === 'facebook' && c.status === 'sent',
  );
  const firedGA = contact.conversions.some(
    (c) => c.platform === 'google_analytics' && c.status === 'sent',
  );
  const fired = firedGoogle || firedMeta || firedGA;

  const location = [contact.from_city, contact.from_country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-0">
      {/* Metadata strip */}
      <div className="pb-5 text-sm text-dark/80">
        <span className="font-mono">
          {formatFullTimestamp(contact.created_at)}
        </span>
        {contact.profile_name && (
          <>
            <span className="mx-1.5 opacity-40">·</span>
            <span className="font-semibold text-dark">
              {contact.profile_name}
            </span>
          </>
        )}
        {location && (
          <>
            <span className="mx-1.5 opacity-40">·</span>
            {location}
          </>
        )}
      </div>

      {/* Fired-to strip — shown ONLY when a platform actually fired. When
          nothing fired we hide it on purpose: the row's Conversion column
          already carries the accurate state (paused / not connected / failed /
          —), and the old "Pending — will fire" message was wrong for those
          cases and contradicted that column. See
          REFERENCE/plans/PLAN_CONTACTSPAGE_REFACTOR.md decision 3. */}
      {fired && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dark/10 pt-5 text-sm text-dark">
          <span className="text-xs font-bold uppercase tracking-wider">
            Fired to
          </span>
          {firedGoogle && (
            <span className="inline-flex items-center gap-1">
              <span className="font-bold text-green-600">✓</span> Google Ads
            </span>
          )}
          {firedMeta && (
            <span className="inline-flex items-center gap-1">
              <span className="font-bold text-green-600">✓</span> Meta Ads
            </span>
          )}
          {firedGA && (
            <span className="inline-flex items-center gap-1">
              <span className="font-bold text-green-600">✓</span> Google Analytics
            </span>
          )}
        </div>
      )}

      {/* Attribution */}
      <div className="border-t border-dark/10 pt-5">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-dark">
          Attribution
        </h3>
        <dl className="mb-4 space-y-2">
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-dark/70">Keyword</dt>
            <dd className="font-mono text-sm text-dark">
              "{contact.term || EMPTY}"
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-dark/70">Landing page</dt>
            <dd className="font-mono text-sm text-dark">
              {contact.landing_page || EMPTY}
            </dd>
          </div>
        </dl>
        <div className="border-t border-dark/5 pt-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-dark">
            Raw
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
            <RawField label="source" value={contact.source} />
            <RawField label="medium" value={contact.medium} />
            <RawField label="channel" value={contact.channel} />
            <RawField label="endpoint" value={contact.endpoint} />
            {contact.term && <RawField label="term" value={contact.term} />}
            {contact.content && (
              <RawField label="content" value={contact.content} />
            )}
            {contact.gclid && (
              <RawField label="gclid" value={contact.gclid} truncate />
            )}
            {contact.fbclid && (
              <RawField label="fbclid" value={contact.fbclid} truncate />
            )}
          </div>
        </div>
      </div>

      {contact.notes && (
        <div className="border-t border-dark/10 pt-5">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-dark">
            Notes
          </h3>
          <p className="whitespace-pre-wrap text-sm text-dark">
            {contact.notes}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-4 border-t border-dark/10 pt-5">
        <a
          href={`tel:${contact.phone}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-dark/70 transition-colors hover:text-dark"
        >
          <PhoneIcon className="h-4 w-4" />
          Call
        </a>
        <a
          href={`https://wa.me/${contact.phone.replace(/[^\d]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-dark/70 transition-colors hover:text-dark"
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp
        </a>
      </div>
    </div>
  );
}

function RawField({
  label,
  value,
  truncate,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <span className="font-medium text-dark/70">{label}</span>
      <span
        className={`font-mono text-dark ${truncate ? 'max-w-[180px] truncate' : ''}`}
        title={truncate ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
}
